import { Backdrop, Drawer, DrawerProps } from "@mui/material";
import { PropsWithChildren, ReactNode, useState } from "react";

interface BTDrawerProps {
  anchor?: "right" | "top" | "left" | "bottom";
  variant?: string;
  open?: boolean;
  showToggle?: boolean;
  onToggle?: () => void;
  toggleIcon?: ReactNode;
}

const BTDrawer = (props: PropsWithChildren<BTDrawerProps> & DrawerProps) => {
  const [isHover, setIsHover] = useState<boolean>(false);
  
  const { showToggle, onToggle, toggleIcon, anchor = "right", ...drawerProps } = props;

  // Determine toggle button position based on drawer anchor
  const getToggleButtonStyle = () => {
    const baseStyle = {
      position: "fixed" as const,
      zIndex: 1300,
      backgroundColor: "var(--tasks-group-bg, #fff)",
      border: "1px solid var(--border-color, #e0e0e0)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      transition: "all 0.3s ease",
      width: "48px",
      height: "48px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    };

    switch (anchor) {
      case "left":
        return {
          ...baseStyle,
          left: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: "0 24px 24px 0",
          borderLeft: "none",
          borderRight: "1px solid var(--border-color, #e0e0e0)",
        };
      case "right":
        return {
          ...baseStyle,
          right: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: "24px 0 0 24px",
          borderRight: "none",
          borderLeft: "1px solid var(--border-color, #e0e0e0)",
        };
      case "top":
        return {
          ...baseStyle,
          top: "0px",
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: "0 0 24px 24px",
          borderTop: "none",
          borderBottom: "1px solid var(--border-color, #e0e0e0)",
        };
      case "bottom":
        return {
          ...baseStyle,
          bottom: "0px",
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: "24px 24px 0 0",
          borderBottom: "none",
          borderTop: "1px solid var(--border-color, #e0e0e0)",
        };
      default:
        return {
          ...baseStyle,
          right: "0px",
          top: "50%",
          transform: "translateY(-50%)",
          borderRadius: "24px 0 0 24px",
          borderRight: "none",
          borderLeft: "1px solid var(--border-color, #e0e0e0)",
        };
    }
  };

  return (
    <>
      <Drawer
        PaperProps={{
          sx: {
            width: props.variant === 'temporary' ? "100%" : "auto",
            border: "0 !important",
            boxShadow: '0 0 20px 0 rgb(0 55 80 / 16%)',
            background: 'var(--tasks-group-bg)'
          }
        }}
        {...drawerProps}
        anchor={anchor}
        hideBackdrop={!isHover}
        variant={props.variant ? props.variant : 'persistent'}
      >
        <Backdrop open={isHover} />
        <div
          style={{
            height: "100%",
            zIndex: 9999
          }}
          onMouseEnter={() => setIsHover(true)}
          onMouseUp={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          {props.open && props.children}
        </div>
      </Drawer>

      {/* Toggle Button - Only show when drawer is closed */}
      {showToggle && onToggle && !props.open && (
        <div
          onClick={onToggle}
          style={getToggleButtonStyle()}
          aria-label="Open drawer"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onToggle();
            }
          }}
        >
          {toggleIcon || (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* Menu icon (hamburger) - always show since button only appears when closed */}
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </div>
      )}
    </>
  );
};
export default BTDrawer;
